import { format } from 'date-fns'
import type { LimitsResponse } from '../api/analytics'

interface UsageCardProps {
  data: LimitsResponse
}

export default function UsageCard({ data }: UsageCardProps) {
  if (!data || !data.limits || !data.plan) {
    return (
      <div className="bg-noir-secondary border border-verdict-rejected/30 rounded-lg p-6">
        <p className="text-verdict-rejected text-sm">Invalid usage data received.</p>
      </div>
    )
  }

  const { limits, plan } = data

  if (!limits.daily) {
    return (
      <div className="bg-noir-secondary border border-verdict-rejected/30 rounded-lg p-6">
        <p className="text-verdict-rejected text-sm">Incomplete usage data received.</p>
      </div>
    )
  }

  const formatUsage = (used: number, limit: number) => {
    return `${used.toLocaleString()} / ${limit.toLocaleString()}`
  }

  const calculatePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0
    const percentage = (used / limit) * 100
    return Math.min(percentage, 100)
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-verdict-rejected'
    if (percentage >= 75) return 'bg-verdict-inconclusive'
    return 'bg-verdict-approved'
  }

  const formatResetTime = (resetsAt: string) => {
    const resetDate = new Date(resetsAt)
    const now = new Date()
    const hoursUntilReset = Math.ceil((resetDate.getTime() - now.getTime()) / (1000 * 60 * 60))
    if (hoursUntilReset < 24) return `Resets in ${hoursUntilReset}h`
    return `Resets ${format(resetDate, 'MMM d')}`
  }

  const dailyPercentage = calculatePercentage(limits.daily.used, limits.daily.limit)

  return (
    <div className="bg-noir-secondary border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-text-primary">Usage Overview</h2>
        <span className="text-sm font-medium text-accent-cyan">{plan.name} Plan</span>
      </div>

      {/* Daily Usage */}
      <div className="mb-6 animate-slide-in-left">
        <div className="mb-3">
          <p className="text-text-secondary text-sm uppercase tracking-wide font-mono mb-1">Daily Requests</p>
          <p className="text-text-primary text-2xl font-mono font-semibold">
            {formatUsage(limits.daily.used, limits.daily.limit)}
          </p>
        </div>
        <div
            role="progressbar"
            aria-valuenow={Math.round(dailyPercentage)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Daily usage: ${limits.daily.used} of ${limits.daily.limit} requests (${Math.round(dailyPercentage)}%)`}
            className="w-full bg-noir-tertiary rounded-full h-3 overflow-hidden mb-2"
          >
            <div
              className={`h-3 rounded-full animate-fill-progress ${getProgressColor(dailyPercentage)}`}
              style={{ width: `${dailyPercentage}%` }}
            />
          </div>
        <p className="text-text-secondary text-sm">{formatResetTime(limits.daily.resets_at)}</p>
      </div>

      {/* Additional Plan Info */}
      <div className="pt-6 border-t border-border grid grid-cols-2 gap-4 text-sm">
        <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <p className="text-text-secondary">Max Lines</p>
          <p className="font-medium text-text-primary font-mono">
            {limits.max_lines.toLocaleString()}
          </p>
        </div>
        <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <p className="text-text-secondary">Rate Limit</p>
          <p className="font-medium text-text-primary font-mono">{limits.per_minute}/min</p>
        </div>
      </div>
    </div>
  )
}
